/* eslint-disable max-lines-per-function */
import classNames from 'clsx';
import { ReactComponent as FolderIcon } from 'img/icons/folder.svg';
import { ReactComponent as FolderFadeIcon } from 'img/icons/folderFade.svg';
import { ReactComponent as FolderPeopleIcon } from 'img/icons/folderPeople.svg';
import escapeRegExp from 'lodash.escaperegexp';
import { equals } from 'ramda';
import React, { memo, useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { getItemById, isMountedOrSharedFolder, isThisOrParentPublicFolder } from 'reactApp/modules/storage/storage.selectors';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { RootState } from 'reactApp/store';
import { DropNotify } from 'reactApp/ui/DropNotify/DropNotify';
import { Hint } from 'reactApp/ui/Hint/Hint';
import { Link } from 'reactApp/ui/Link/Link';
import { ETreeNodeMode } from 'reactApp/ui/TreeComponent/TreeComponent.constants';
import { ETreeRootIds } from 'reactApp/ui/TreeComponent/TreeComponent.data';
import { ITreeNodeClickData, TreeNodeData } from 'reactApp/ui/TreeComponent/TreeNode.types';
import { ChevronRightIcon } from 'reactApp/ui/VKUIIcons';
import { sendXray } from 'reactApp/utils/ga';
import { noop } from 'reactApp/utils/helpers';

import { useDragAndDrop } from './hooks/useDragAndDrop';
import { getMoveText } from './TreeComponent.helpers';
import styles from './TreeNode.css';

interface Props {
    selectedId: string;
    selectedStorage?: EStorageType;
    mod?: ETreeNodeMode;
    data: TreeNodeData;
    onExpand: ({ id, gaId, storage, isOpen, level, selectedStorage }) => Promise<void>;
    onClick: (data: ITreeNodeClickData) => void;
    level?: number;
    forceOpen?: boolean;
    isMobile?: boolean;
    // Ref на выбранный элемент
    handleScrollIntoViewRef?: (RefObject) => void;
    maxTreeLevel?: number;
    search?: string;
}

const areEqual = (prevProps: Props, nextProps: Props): boolean =>
    prevProps.selectedId === nextProps.selectedId &&
    prevProps.data.itemsCounter === nextProps.data.itemsCounter &&
    equals(prevProps.data, nextProps.data);

const getTreeMargin = (level: number) => `${22 * level}px`;

const iconComponentMap = {
    mountFolder: <FolderPeopleIcon width={16} height={16} data-qa-id="mountFolder" />,
    folder: <FolderIcon width={16} height={16} data-qa-id="folder" />,
};

/* eslint-disable complexity */
export const TreeNode = memo<Props>(
    ({
        data,
        selectedId,
        mod,
        onExpand = () => Promise.resolve(),
        onClick = noop,
        level = 0,
        forceOpen = false,
        isMobile = false,
        selectedStorage = EStorageType.home,
        handleScrollIntoViewRef = noop,
        maxTreeLevel,
		search = '',
    }) => {
        const { id, gaId, folderCounter, title, children, itemsCounter, icon, storage, href, component, isMountedOrShared } = data;
        const item = useSelector((state: RootState) => getItemById(state, id));
        const { is: isSharedOrMounted } = useSelector((state: RootState) => isMountedOrSharedFolder(state, item));
        const isThisOrParentPublic = useSelector((state: RootState) => isThisOrParentPublicFolder(state, item));
        const forceOpenHomeRoot = id === '/' && selectedStorage === EStorageType.home;
        const isAttaches = selectedStorage === EStorageType.attaches;
        const isMatched =
            !!selectedId?.match(`${escapeRegExp(isAttaches ? `attaches${id}` : id)}(/|$)`) ||
            (id === '/incoming' && selectedStorage === EStorageType.sharedLinks);
        const forceOpenFoldersOnThePath =
            (isMatched || forceOpenHomeRoot) && selectedId !== `${EStorageType.home}/` && selectedStorage !== EStorageType.start;
        const [isOpen, setOpen] = useState<boolean | null>(forceOpen || forceOpenFoldersOnThePath || null);
        const [isLoading, setLoading] = useState(false);

        const levelMargin = getTreeMargin(level);
        const levelMarginNext = getTreeMargin(level + 1);
        const isExpandable = folderCounter;
        const isSelected = storage + id === selectedId;

        const isSelectDialog = mod === ETreeNodeMode.selectDlg;
        const folderType = isMountedOrShared ? 'mountFolder' : 'folder';

        const loaderItems =
            (isOpen || forceOpenFoldersOnThePath) && isLoading && folderCounter ? Array.from(new Array(folderCounter).keys()) : null;

        const noLink = id === ETreeRootIds.domain || isSelectDialog;

        const handleExpand = useCallback(
            (e: React.MouseEvent<HTMLElement>) => {
                e.stopPropagation();

                if (maxTreeLevel && level >= maxTreeLevel) {
                    return;
                }

                const newIsOpen = !isOpen;

                setOpen(newIsOpen);

                if (newIsOpen) {
                    setLoading(true);
                }

                onExpand({ isOpen: newIsOpen, id, storage, gaId, level, selectedStorage }).finally(() => {
                    setLoading(false);
                });
            },
            [id, gaId, storage, level, isOpen, setOpen, setLoading, selectedStorage, onExpand]
        );

        const handleClick = useCallback(
            (e: React.MouseEvent<HTMLElement>) => {
                e.stopPropagation();
                if (!isOpen && isExpandable) {
                    handleExpand(e);
                }
            },
            [isOpen, isExpandable, handleExpand]
        );

        const handleGoTo = useCallback(
            (e: React.MouseEvent<HTMLElement>) => {
                e.stopPropagation();

                onClick({ id, gaId, storage, href, level, isSharedOrMounted, isThisOrParentPublic });

                if (!isOpen && isExpandable) {
                    handleExpand(e);
                }
            },
            [id, gaId, href, storage, level, isOpen, isSharedOrMounted, isThisOrParentPublic, onClick, handleExpand, isExpandable]
        );

        const handleDoubleClick = useCallback(
            (e: React.MouseEvent<HTMLElement>) => {
                e.stopPropagation();

                onClick({ id, gaId, storage, href, level, isSharedOrMounted, isThisOrParentPublic });

                if (isExpandable) {
                    handleExpand(e);
                }
            },
            [id, gaId, href, storage, level, onClick, isExpandable, isSharedOrMounted, isThisOrParentPublic, handleExpand]
        );

        const { isDraggingOver, handleMouseMove, handleMouseLeave, handleStopDrag, isAllowedToDrop } = useDragAndDrop(data);

        const handleExpandClick = useCallback(
            (e: React.MouseEvent<HTMLElement>) => {
                e.preventDefault();
                handleExpand(e);
            },
            [handleExpand]
        );

        useEffect(() => {
            if (!loaderItems) {
                sendXray([`${isSelectDialog ? 'dlg' : 'lm'}-tree-folder`, 'show', level.toString(), (!!isExpandable).toString()]);
            }
        }, [loaderItems, level, maxTreeLevel, isExpandable, mod]);

        if (maxTreeLevel && level > maxTreeLevel) {
            return null;
        }

        const renderChildren = () => {
            if (component) {
                return <>{component}</>;
            }

            return loaderItems
                ? loaderItems.map((item) => (
                      <div className={classNames(styles.item, styles.loaderItem)} key={item}>
                          <div className={styles.space} style={{ width: levelMarginNext }} />
                          <div className={styles.expandControl} />
                          <div className={styles.folderIcon}>
                              <FolderFadeIcon width={20} height={20} />
                          </div>
                          <div className={styles.titlePlaceholder} />
                      </div>
                  ))
                : !!children &&
                      children.map((item) => (
                          <TreeNode
                              key={item.id}
                              selectedId={selectedId}
                              data={item}
                              mod={mod}
                              search={search}
                              onExpand={onExpand}
                              onClick={onClick}
                              level={level + 1}
                              selectedStorage={selectedStorage}
                              handleScrollIntoViewRef={handleScrollIntoViewRef}
                              maxTreeLevel={maxTreeLevel}
                          />
                      ));
        };

        return (
            <div className={classNames({ [styles.root]: true, [styles[`root_${mod}`]]: !!mod })} data-name={id}>
                <Link id={id} href={href} storage={storage} handleGoTo={handleGoTo} handleClick={handleClick} noLink={noLink}  search={search}>
                    <div
                        className={classNames(styles.item, { [styles.selected]: isSelected })}
                        onDoubleClick={isSelectDialog && !isMobile ? handleDoubleClick : noop}
                        ref={isSelected ? handleScrollIntoViewRef : undefined}
                        onMouseMove={isSelectDialog ? noop : handleMouseMove}
                        onMouseLeave={isSelectDialog ? noop : handleMouseLeave}
                        onMouseUp={isSelectDialog ? noop : handleStopDrag}
                        data-id={id}
                    >
                        <div className={styles.space} style={{ width: levelMargin }} />
                        <div
                            className={classNames(styles.expandControl, { [styles.expandControlExpandable]: isExpandable })}
                            onClick={isExpandable ? handleExpandClick : noop}
                        >
                            {Boolean(folderCounter) && (
                                <ChevronRightIcon
                                    className={classNames(styles.expandIcon, {
                                        [styles.expandIconExpanded]:
                                            (!maxTreeLevel || level < maxTreeLevel) &&
                                            (isOpen || (forceOpenFoldersOnThePath && isOpen !== false)),
                                    })}
                                />
                            )}
                        </div>
                        <div className={styles.folderIcon}>{icon || iconComponentMap[folderType]}</div>
                        <Hint text={level > 0 ? title : ''}>
                            <div className={styles.title}>{title}</div>
                        </Hint>
                        {!!itemsCounter && (
                            <div className={styles.counter}>
                                <div className={styles.counterVal}>{itemsCounter}</div>
                            </div>
                        )}

                        {!isSelectDialog && (
                            <DropNotify
                                text={getMoveText(isAllowedToDrop, data.storage)}
                                isError={data.storage === EStorageType.trashbin}
                                isWarning={!isAllowedToDrop}
                                isDraggingOver={isDraggingOver}
                            />
                        )}
                    </div>
                </Link>
                <div className={styles.children}>{(isOpen || (forceOpenFoldersOnThePath && isOpen !== false)) && renderChildren()}</div>
            </div>
        );
    },
    areEqual
);

TreeNode.displayName = 'TreeNode';

TreeNode.whyDidYouRender = true;
